import { Button, Grid, Typography } from "@mui/material";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EventObject } from "../../../redux/Hire/candidateMeetingApi";
import EditableDraftSummary from "./EditableDraftSummary";
import TranscriptDrawer from "./TranscriptDrawer";
// ...existing code...
import Plyr from "plyr-react";
import React from "react";
import {
  useGetVideoQuery,
  useLazyGetInterviewQuery,
} from "../../../redux/Hire/interviewQuestionSlice";
import { CandidateDetails } from "../../../redux/Hire/jobRequestApi";
import NotesSectionList from "./NotesSectionList";

type Props = {
  candidate?: CandidateDetails;
  meeting?: EventObject;
  jobId?: number;
};

const POLL_INTERVAL = 5000; // Polling interval in milliseconds

const CandidateSummary = ({ candidate, meeting, jobId }: Props) => {
  const [getInterview, { data }] = useLazyGetInterviewQuery();
  const pollRef = useRef<NodeJS.Timeout | null>(null);

  const meetingStart = meeting?.meetingStartedOn
    ? new Date(meeting.meetingStartedOn).getTime()
    : null;
  const meetingEnd = meeting?.meetingEndedOn
    ? new Date(meeting.meetingEndedOn).getTime()
    : null;

  useEffect(() => {
    const now = Date.now();
    if (meetingEnd && meetingEnd < now && candidate?.id && jobId) {
      getInterview({
        candidateId: candidate.id.toString(),
        jobId: jobId?.toString() || "",
      });
      pollRef.current = setInterval(() => {
        getInterview({
          candidateId: candidate.id.toString(),
          jobId: jobId?.toString() || "",
        });
      }, POLL_INTERVAL);
      return () => {
        if (pollRef.current) clearInterval(pollRef.current);
      };
    }
    // Cleanup if not polling
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [candidate?.id, meetingEnd, getInterview, jobId, meeting?.jobId]);

  // Stop polling if draftSummary is available
  useEffect(() => {
    if (data?.draftSummary && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [data?.draftSummary]);

  // // If meeting is in the future, show message
  const now = Date.now();

  const meetingInfoFromInterview = useMemo(() => {
    return data?.meetings.find(
      (meetingInfo) => meetingInfo.link === meeting?.link,
    );
  }, [data?.meetings, meeting?.link]);

  const { data: videoData } = useGetVideoQuery(
    { meetingId: meetingInfoFromInterview?.meetingId.toUpperCase() || "" },
    {
      skip: !meetingInfoFromInterview?.meetingId,
    },
  );

  // Transcript Drawer state
  const [transcriptOpen, setTranscriptOpen] = useState(false);

  // Plyr ref for seeking
  const plyrRef = useRef<any>(null);

  // Seek handler for transcript
  const handleSeek = useCallback((seconds: number) => {
    if (plyrRef.current && plyrRef.current.plyr) {
      plyrRef.current.plyr.currentTime = seconds;
      plyrRef.current.plyr.play();
    }
  }, []);

  const MemoPlyr = React.useMemo(
    () =>
      videoData?.videoUrl ? (
        <Plyr
          width={"auto"}
          ref={plyrRef}
          source={{
            type: "video",
            sources: [
              {
                src: videoData?.videoUrl || "",
                provider: "html5",
              },
            ],
          }}
          options={{
            controls: [
              "play",
              "progress",
              "current-time",
              "mute",
              "volume",
              "fullscreen",
            ],
            autoplay: false,
          }}
        />
      ) : null,
    [videoData?.videoUrl],
  );

  return (
    <Grid container direction="column" gap={4}>
      {/* Chapters as chips above video */}

      {MemoPlyr}
      {meetingInfoFromInterview &&
        meetingInfoFromInterview.keywords &&
        meetingInfoFromInterview.keywords.length > 0 && (
          <Grid container item gap={2} wrap="nowrap" alignItems={"center"}>
            <Typography color={"#1E293A"} fontSize={"18px"} fontWeight={"600"}>
              Keywords
            </Typography>
            <Grid container item gap={2}>
              {meetingInfoFromInterview.keywords.map((keyword, index) => (
                <Typography
                  key={index}
                  bgcolor={"#FAF9F9"}
                  borderRadius={"6px"}
                  px={2}
                  color={"#000000"}
                  fontSize={"16px"}
                  textTransform={"capitalize"}
                  lineHeight={"22px"}
                >
                  {keyword}
                </Typography>
              ))}
            </Grid>
          </Grid>
        )}
      {meetingStart && meetingStart > now && (
        <Grid item>
          <Typography color="#3F4871" fontSize={24} fontWeight={700}>
            Summary will be generated after an interview with candidate.
          </Typography>
        </Grid>
      )}
      {meetingEnd && meetingEnd < now && !data?.draftSummary && (
        <Grid item>
          <Typography color="#3F4871" fontSize={24} fontWeight={700}>
            Summary will be generated in some time after you have submitted the
            interview.
          </Typography>
        </Grid>
      )}
      {data?.draftSummary && (
        <Grid item>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item xs>
              <EditableDraftSummary
                initialSummary={data.interviewSummary || data.draftSummary}
                interviewId={data._id}
                extraAction={
                  meetingInfoFromInterview?.transcript &&
                  meetingInfoFromInterview.transcript.length > 0 && (
                    <Grid item>
                      <Button
                        variant="text"
                        color="primary"
                        size="small"
                        onClick={() => setTranscriptOpen(true)}
                      >
                        View Transcript
                      </Button>
                    </Grid>
                  )
                }
              />
            </Grid>
          </Grid>
          <TranscriptDrawer
            open={transcriptOpen}
            onClose={() => setTranscriptOpen(false)}
            transcript={meetingInfoFromInterview?.transcript || []}
            onSeek={handleSeek}
          />
        </Grid>
      )}

      {meetingInfoFromInterview &&
        meetingInfoFromInterview.shorthandBullet !== "" && (
          <Grid item>
            <Typography
              color={"#1E293A"}
              fontSize={"18px"}
              fontWeight={"600"}
              mb={1}
            >
              Notes
            </Typography>
            <NotesSectionList
              shorthandBullet={meetingInfoFromInterview?.shorthandBullet || ""}
              onSeek={handleSeek}
            />
          </Grid>
        )}
    </Grid>
  );
};

export default CandidateSummary;
